import { apiRequest } from "@/lib/queryClient";
import type { MartinaTerminalContext } from "@/components/exai/ExAiGlobalDrawer";

const ENTITY_ENDPOINT: Record<string, (id: string) => string> = {
  contact: (id) => `/api/contacts/${id}`,
  company: (id) => `/api/companies/${id}`,
  deal: (id) => `/api/deals/${id}`,
  lead: (id) => `/api/leads/${id}`,
  branch: (id) => `/api/branches/${id}`,
  meeting: (id) => `/api/meetings/${id}`,
  task: (id) => `/api/tasks/${id}`,
  email_message: (id) => `/api/emails/${id}`,
  brand: (id) => `/api/brands/${id}`,
};

const GENERIC_TITLE: Record<string, string> = {
  contact: "Contatto", company: "Azienda", deal: "Opportunità", lead: "Lead",
  branch: "Filiale", meeting: "Appuntamento", task: "Attività",
  email_message: "Email", email_thread: "Conversazione email", file: "File", brand: "Brand",
};

function recordLabel(entityType: string, value: Record<string, unknown>): string | null {
  const firstName = typeof value.firstName === "string" ? value.firstName.trim() : "";
  const lastName = typeof value.lastName === "string" ? value.lastName.trim() : "";
  const person = [firstName, lastName].filter(Boolean).join(" ");
  if (person) return person;
  const fields = entityType === "email_message"
    ? [value.subject]
    : [value.name, value.title, value.companyName, value.filename, value.fileName];
  return fields.find((field): field is string => typeof field === "string" && Boolean(field.trim()))?.trim() ?? null;
}

export function genericMartinaTerminalTitle(entityType?: string): string {
  return entityType ? (GENERIC_TITLE[entityType] ?? "Contesto CRM") : "Contesto CRM";
}

export async function resolveHumanMartinaTerminalContext(
  context: MartinaTerminalContext,
): Promise<MartinaTerminalContext> {
  const entityType = context.entityType;
  const entityId = context.entityId;
  if (!entityType || !entityId) return context;
  const endpoint = ENTITY_ENDPOINT[entityType];
  if (!endpoint) return {
    ...context,
    title: context.title?.replace(/\s*#?\d+\s*$/, "") || genericMartinaTerminalTitle(entityType),
  };
  try {
    const value = await apiRequest(endpoint(String(entityId)), { method: "GET", notifyError: false });
    if (!value || typeof value !== "object" || Array.isArray(value)) return context;
    const label = recordLabel(entityType, value as Record<string, unknown>);
    if (!label) return context;
    const applyLabel = (ref: MartinaTerminalContext["primary"]) => ref && ref.type === entityType && ref.id === String(entityId)
      ? { ...ref, label }
      : ref;
    return {
      ...context,
      title: label,
      primary: applyLabel(context.primary),
      selected: applyLabel(context.selected),
      origin: context.origin ? { ...context.origin, label } : context.origin,
    };
  } catch {
    return context;
  }
}
